﻿import { assert } from '@fe/core';
import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Editor, EditorHandle, EditorTools, EditorUtils } from '@progress/kendo-react-editor';
import { Loader } from '@progress/kendo-react-indicators';
import { Avatar } from '@progress/kendo-react-layout';
import clsx from 'clsx';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { createRef, useEffect, useMemo, useState } from 'react';
import { FlightDetailsModel } from '../../../../flights/interop/flightDto';
import { DateTimeFormatter } from '../../../../formatters';
import useUserRights from '../../../../hooks/use-user-rights';
import { AddOutline, ClockOutline, EditOutline, TrashOutline, WarningTriangleOutline } from '../../../../icons/icons';
import { sanitizeHtmlContent } from '../../../../services/html-service';
import { FlightNoteTimeSpan, Note, NoteCategory, NoteType } from '../../../../types/notes';
import PulseHighlight from '../../../components/inputs/PulseHighlight';
import SegmentedControl from '../../../components/inputs/SegmentedControl';
import { useAppDispatch, useAppSelector } from '../../../hooks/store-hooks';
import { notesSelector } from '../../../state/selectors';
import {
    createNote as addNoteAction,
    deleteNote as deleteNoteAction,
    updateNote as editNoteAction,
    fetchNotesForItem,
    fetchNotesForItems
} from '../../../state/slices/notes-slice';
import { useFormRadioValue } from '../hooks/forms';

dayjs.extend(relativeTime);

const {
    Bold,
    Italic,
    Underline,
    Strikethrough,
    Subscript,
    Superscript,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
    Indent,
    Outdent,
    OrderedList,
    UnorderedList,
    Undo,
    Redo,
    FontSize,
    FontName,
    FormatBlock,
    Link,
    Unlink,
    ViewHtml
} = EditorTools;

type avatarColor = 'base' | 'primary' | 'secondary' | 'tertiary';
const avatarColors: avatarColor[] = ['tertiary', 'base', 'primary', 'tertiary'];

export interface NotesProps {
    showHeader?: boolean;
    itemId: string | null;
    additionalItemId?: string;
    category: NoteCategory;
    flight?: FlightDetailsModel;
    onNotesDeleted?: () => void;
}

export interface OpenNotesCommand extends NotesProps {
    flightId?: number;
}

export default function Notes(props: NotesProps) {
    const busy = useAppSelector((state) => state.notes.busy);
    const user = useAppSelector((x) => x.user.info);
    assert(user.isAuthenticated);
    const userRights = useUserRights();
    const dispatch = useAppDispatch();
    const userCompany = useFormRadioValue(
        [
            { value: NoteType.user, label: 'Personal' },
            { value: NoteType.company, label: 'Company' }
        ],
        NoteType.user
    );
    const legSpecific = useFormRadioValue(
        [
            { value: FlightNoteTimeSpan.persistent, label: 'Persistent' },
            { value: FlightNoteTimeSpan.flightLeg, label: 'Flight leg specific' }
        ],
        FlightNoteTimeSpan.persistent
    );
    const [userNotes, setUserNotes] = useState<Note[]>([]);
    const [companyNotes, setCompanyNotes] = useState<Note[]>([]);
    const [showEditor, setShowEditor] = useState(false);
    const [editedNote, setEditedNote] = useState<Note | null>(null);
    const [noteForDeletion, setNoteForDeletion] = useState<Note | null>(null);
    const editor = createRef<EditorHandle>();

    const ids = useMemo(() => {
        return [props.itemId, props.additionalItemId].filter((id): id is string => id !== undefined && id !== null);
    }, [props.itemId, props.additionalItemId]);

    const notes = useAppSelector((state) => notesSelector(state, ids));

    useEffect(() => {
        if (showEditor && editedNote && editor.current?.view) {
            requestAnimationFrame(() => {
                if (editor.current?.view) {
                    EditorUtils.setHtml(editor.current.view, editedNote.content);
                }
            });
        }
    }, [editedNote, showEditor]);

    useEffect(() => {
        if (props.additionalItemId) {
            dispatch(fetchNotesForItems({ itemIds: [props.itemId!, props.additionalItemId] }));
        } else {
            dispatch(fetchNotesForItem({ itemId: props.itemId! }));
        }
    }, [props.itemId, props.additionalItemId, dispatch]);

    useEffect(() => {
        let userNotes: Note[] = [];
        let companyNotes: Note[] = [];
        if (notes && notes.length > 0) {
            userNotes = notes.filter((note) => !note.isCompany);
            companyNotes = notes.filter((note) => note.isCompany);
        }
        setCompanyNotes(companyNotes);
        setUserNotes(userNotes);
    }, [notes]);

    const [showBusy, setShowBusy] = useState(false);
    useEffect(() => {
        let timeout: number;
        if (busy) {
            timeout = self.setTimeout(() => setShowBusy(true), 500);
        } else {
            setShowBusy(false);
        }
        return () => {
            self.clearTimeout(timeout);
        };
    }, [busy]);

    const closeEditor = () => {
        setEditedNote(null);
        setShowEditor(false);
        if (editor.current?.view) {
            EditorUtils.setHtml(editor.current.view, '');
        }
        legSpecific.onChange({
            syntheticEvent: null,
            nativeEvent: null,
            value: FlightNoteTimeSpan.persistent
        } as any);
    };

    const addNote = () => {
        let content = '';
        if (editor.current?.view) {
            content = EditorUtils.getHtml(editor.current.view.state);
        }
        dispatch(
            addNoteAction({
                note: {
                    itemId: props.itemId!,
                    category: props.category,
                    content: content,
                    dateCreated: Date.now(),
                    createdBy: user.name,
                    isCompany: userCompany.value === NoteType.company,
                    origin: legSpecific.value === FlightNoteTimeSpan.flightLeg ? props.flight?.origin : null,
                    departureDateTime: legSpecific.value === FlightNoteTimeSpan.flightLeg ? props.flight?.departureTime : null,
                    eta: legSpecific.value === FlightNoteTimeSpan.flightLeg ? props.flight?.arrivalTime : null
                }
            })
        );
        closeEditor();
    };

    const editNote = () => {
        dispatch(
            editNoteAction({
                note: {
                    ...editedNote!,
                    content: editor.current?.view ? EditorUtils.getHtml(editor.current.view.state) : editedNote!.content,
                    dateEdited: Date.now(),
                    editedBy: user.name
                }
            })
        );
        closeEditor();
    };

    const deleteNote = (note: Note) => {
        dispatch(deleteNoteAction({ note }));
        props.onNotesDeleted?.();
    };

    const getTitle = () => {
        let userCompanyText = userCompany.value === NoteType.company ? 'company' : 'personal';
        if (!userRights.companyFeaturesEnabled) {
            userCompanyText = '';
        }
        return `${editedNote ? 'Edit' : 'Add new'} ${userCompanyText} note`;
    };

    const isFlightLegSpecific = (note: Note) => {
        return !!(note.origin && note.departureDateTime && note.eta);
    };

    const itemCanBeFlightLegSpecific = () => {
        return !!(
            props.category === NoteCategory.aircraft &&
            props.flight &&
            props.flight.origin &&
            props.flight.departureTime &&
            props.flight.arrivalTime
        );
    };

    const getAvatarColor = (name: string) => {
        if (name === user.name) {
            return 'primary';
        }

        const charCodeSum = name.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
        return avatarColors[charCodeSum % avatarColors.length];
    };

    return (
        <>
            <div className={'p-1 w-100 d-flex flex-column'} style={{ minWidth: '500px', flexShrink: 1, minHeight: 0 }}>
                <div
                    className={clsx(
                        'd-flex justify-content-between align-items-start align-center pb-2 p-1',
                        props.showHeader && 'border-bottom'
                    )}>
                    <div className="d-flex flex-column gap-2">
                        {props.showHeader && (
                            <div className={'k-font-weight-medium !k-font-size-lg'}>
                                {props.itemId !== null ? props.itemId : 'Select an item to view notes'}
                            </div>
                        )}
                        {userRights.companyFeaturesEnabled && (
                            <SegmentedControl
                                {...userCompany}
                                disabled={!userRights.canEditCompanyNotes}
                                indicators={{
                                    [NoteType.user]: userNotes.length,
                                    [NoteType.company]: companyNotes.length
                                }}></SegmentedControl>
                        )}
                        {!userRights.companyFeaturesEnabled && (
                            <span className={'text-secondary !k-font-size-sm'}>{`${userNotes.length} notes`}</span>
                        )}
                    </div>
                    <PulseHighlight active={notes.length <= 0 && !!props.itemId && !showEditor && !busy}>
                        <Button svgIcon={AddOutline} disabled={!props.itemId} themeColor="primary" onClick={() => setShowEditor(true)}>
                            Add Note
                        </Button>
                    </PulseHighlight>
                </div>
                <div className={'d-flex flex-column gap-3 k-overflow-y-auto flight-details-scrollbars p-1 mt-1'}>
                    {showBusy && (
                        <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center text-secondary my-4">
                            <div className="k-font-size-lg k-font-weight-medium mb-2">Loading notes...</div>
                            <Loader></Loader>
                        </div>
                    )}
                    {!busy &&
                        notes &&
                        notes.length > 0 &&
                        (userCompany.value === NoteType.user ? userNotes : companyNotes)
                            // created then edited notes first
                            .sort((noteA, noteB) => {
                                const dateA = noteA.dateEdited ?? noteA.dateCreated;
                                const dateB = noteB.dateEdited ?? noteB.dateCreated;
                                return dateB - dateA;
                            })
                            .map((note) => (
                                <div key={note.id} className={'k-card p-3 d-flex flex-row justify-content-between flex-shrink-0 border'}>
                                    <div className={'d-flex overflow-x-auto'}>
                                        {note.isCompany && (
                                            <Avatar themeColor={getAvatarColor(note.createdBy)} rounded="full" type="text">
                                                {note.createdBy.slice(0, 2).toUpperCase()}
                                            </Avatar>
                                        )}
                                        <div className={'d-flex flex-column gap-2'}>
                                            <div
                                                className="note-content"
                                                dangerouslySetInnerHTML={{ __html: sanitizeHtmlContent(note.content) }}></div>
                                            <div className={'text-secondary d-flex align-items-center !k-font-size-sm'}>
                                                {[
                                                    dayjs.unix(note.dateCreated).fromNow(),
                                                    note.isCompany && note.createdBy,
                                                    note.dateEdited && `edited ${dayjs.unix(note.dateEdited).fromNow()}`,
                                                    note.dateEdited && note.isCompany && note.editedBy !== note.createdBy && note.editedBy,
                                                    isFlightLegSpecific(note) && (
                                                        <span
                                                            title={`Flight leg specific:${note.origin} ${DateTimeFormatter.formatDateTime(note.departureDateTime)}-${DateTimeFormatter.formatDateTime(note.eta)}`}>
                                                            <SvgIcon themeColor={'warning'} icon={ClockOutline} />
                                                        </span>
                                                    ),
                                                    note.itemId !== props.itemId &&
                                                    props.additionalItemId &&
                                                    note.category === NoteCategory.aircraft ? (
                                                        <div className={'fe-badge-secondary'}>Registration Id</div>
                                                    ) : null
                                                ]
                                                    .filter(Boolean)
                                                    .map((item, index, arr) => (
                                                        <span key={index}>
                                                            {item}
                                                            {index < arr.length - 1 && <span className="mx-1">·</span>}
                                                        </span>
                                                    ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={'d-flex align-items-start justify-content-start gap-1'}>
                                        <Button
                                            size={'small'}
                                            fillMode={'outline'}
                                            svgIcon={EditOutline}
                                            title={'Edit note'}
                                            onClick={() => {
                                                setEditedNote(note);
                                                setShowEditor(true);
                                            }}></Button>
                                        <Button
                                            size={'small'}
                                            fillMode={'outline'}
                                            svgIcon={TrashOutline}
                                            title={'Delete note'}
                                            onClick={() => setNoteForDeletion(note)}
                                            themeColor={'error'}></Button>
                                    </div>
                                </div>
                            ))}
                    {!busy && !props.itemId && (
                        <div className={'d-flex flex-column gap-1 px-3 overflow-y-auto flex-grow-1'}>
                            <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center text-secondary my-4">
                                <div className="k-font-size-lg k-font-weight-medium mb-2">No item selected</div>
                                <div>Select an item on the left to view and add notes.</div>
                            </div>
                        </div>
                    )}
                    {!busy && props.itemId && notes && notes.length === 0 && (
                        <div className={'d-flex flex-column gap-1 px-3 overflow-y-auto flex-grow-1'}>
                            <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center text-secondary my-4">
                                <div className="k-font-size-lg k-font-weight-medium mb-2">No notes found</div>
                                <div>Create a new note to get started.</div>
                            </div>
                        </div>
                    )}
                    {!busy && userNotes.length === 0 && userCompany.value === NoteType.user && notes?.length > 0 && (
                        <div className={'d-flex flex-column gap-1 px-3 overflow-y-auto flex-grow-1'}>
                            <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center text-secondary my-4">
                                <div className="k-font-size-lg k-font-weight-medium mb-2">No personal notes found</div>
                                <div>Try switching to company notes or create a new personal note.</div>
                            </div>
                        </div>
                    )}
                    {!busy && companyNotes.length === 0 && userCompany.value === NoteType.company && notes?.length > 0 && (
                        <div className={'d-flex flex-column gap-1 px-3 overflow-y-auto flex-grow-1'}>
                            <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center text-secondary my-4">
                                <div className="k-font-size-lg k-font-weight-medium mb-2">No company notes found</div>
                                <div>Try switching to personal notes or create a new company note.</div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {showEditor && (
                <Dialog onClose={() => setShowEditor(false)} title={getTitle()} width={'min(800px, 80vw)'}>
                    {props.category === NoteCategory.aircraft && props.flight && (
                        <div className={'pb-2'}>
                            <div className={'d-flex'}>
                                <SegmentedControl disabled={!itemCanBeFlightLegSpecific()} {...legSpecific}></SegmentedControl>
                                {!itemCanBeFlightLegSpecific() && (
                                    <span title={'This flight does not have sufficient data to create a flight leg specific note'}>
                                        <SvgIcon size={'small'} themeColor={'warning'} icon={WarningTriangleOutline} />
                                    </span>
                                )}
                            </div>
                            <div className={'text-secondary pt-1'}>
                                {legSpecific.value === FlightNoteTimeSpan.persistent
                                    ? 'This note will remain until manually deleted'
                                    : 'This note will be automatically removed after the flight finishes'}
                            </div>
                        </div>
                    )}
                    <Editor
                        ref={editor}
                        defaultEditMode="div"
                        tools={[
                            Bold,
                            Italic,
                            Underline,
                            Strikethrough,
                            Subscript,
                            Superscript,
                            AlignLeft,
                            AlignCenter,
                            AlignRight,
                            AlignJustify,
                            Indent,
                            Outdent,
                            OrderedList,
                            UnorderedList,
                            FontSize,
                            FontName,
                            FormatBlock,
                            Link,
                            Unlink,
                            ViewHtml,
                            Undo,
                            Redo
                        ]}></Editor>
                    <DialogActionsBar>
                        {editedNote ? (
                            <Button type="button" onClick={editNote} themeColor={'primary'}>
                                Edit
                            </Button>
                        ) : (
                            <Button type="button" onClick={addNote} themeColor={'primary'}>
                                Add
                            </Button>
                        )}

                        <Button type="button" onClick={closeEditor}>
                            Cancel
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}
            {noteForDeletion && (
                <Dialog title={'Confirm Delete'} onClose={() => setNoteForDeletion(null)}>
                    <p>Are you sure you want to delete this note"?</p>
                    <DialogActionsBar>
                        <Button onClick={() => setNoteForDeletion(null)}>Cancel</Button>
                        <Button
                            themeColor={'error'}
                            onClick={() => {
                                deleteNote(noteForDeletion);
                                setNoteForDeletion(null);
                            }}>
                            Delete
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}
        </>
    );
}
